import { default as React } from 'react';
import { useCallback, useState, useMemo, useEffect } from 'react';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import FormHelperText from '@mui/material/FormHelperText';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { makeStyles } from '@mui/styles';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

const useStyles = makeStyles(theme => ({
  prefixPreview: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    backgroundColor:
      theme.palette.mode === 'dark'
        ? theme.palette.grey[800]
        : theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  prefixLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  prefixValue: {
    fontWeight: 600,
    color: theme.palette.primary.main,
    fontSize: '0.875rem',
    fontFamily: 'monospace',
  },
}));

const REQUIRED_SUFFIX = '-wsky';

/**
 * Extracts the project prefix from a GCP project ID.
 *
 * GCP project IDs must end with -wsky and follow the pattern: {prefix}-{env}-app-wsky
 * where env can be: prod, dev, stage, qa, test, uat, sandbox
 *
 * Examples:
 *   - "plateng-prod-app-wsky" -> "plateng"
 *   - "myproject-dev-app-wsky" -> "myproject"
 */
function extractProjectPrefix(projectId: string): string | null {
  if (!projectId) return null;

  // First check if it ends with the required suffix
  if (!projectId.endsWith(REQUIRED_SUFFIX)) {
    return null;
  }

  // Environment patterns to look for
  const envPatterns = [
    '-prod-',
    '-dev-',
    '-stage-',
    '-qa-',
    '-test-',
    '-uat-',
    '-sandbox-',
  ];

  for (const pattern of envPatterns) {
    const index = projectId.indexOf(pattern);
    if (index > 0) {
      return projectId.substring(0, index);
    }
  }

  return null;
}

/**
 * Custom field component for GCP Project ID input
 * Accepts the complete GCP project ID and extracts the prefix internally
 */
export const GcpProjectIdField = ({
  onChange,
  rawErrors,
  required,
  formData,
  uiSchema,
  idSchema,
  schema,
  formContext,
}: FieldExtensionComponentProps<string>) => {
  const classes = useStyles();
  const [projectId, setProjectId] = useState(formData || '');
  const [validationError, setValidationError] = useState<string>('');

  // Get templateSource and standardTemplate from form context
  const templateSource = formContext?.formData?.templateSource || '';
  const standardTemplate = formContext?.formData?.standardTemplate || '';

  // Determine if this field should be visible
  const shouldBeVisible =
    templateSource === 'standard' &&
    standardTemplate === 'terraform-infrastructure';

  // Extract prefix from the project ID - must be before early return to follow Rules of Hooks
  const extractedPrefix = useMemo(
    () => extractProjectPrefix(projectId),
    [projectId],
  );

  // Handle change - must be declared before early return to follow Rules of Hooks
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = event.target.value;

      // Convert to lowercase
      const lowercaseValue = rawValue.toLowerCase();

      // Validate: lowercase letters, numbers, and hyphens only
      const pattern = /^[a-z][a-z0-9-]*$/;
      if (lowercaseValue && !pattern.test(lowercaseValue)) {
        setValidationError(
          'Project ID must start with a lowercase letter and contain only lowercase letters, numbers, and hyphens.',
        );
      } else {
        setValidationError('');
      }

      setProjectId(lowercaseValue);

      // Extract prefix and pass it as the output value
      const prefix = extractProjectPrefix(lowercaseValue);
      if (prefix) {
        setValidationError('');
        onChange(prefix);
      } else if (lowercaseValue) {
        // If value doesn't match required pattern, show error
        if (!lowercaseValue.endsWith(REQUIRED_SUFFIX)) {
          setValidationError(
            `Project ID must end with "${REQUIRED_SUFFIX}" (e.g., plateng-prod-app-wsky)`,
          );
        } else {
          setValidationError(
            'Could not extract prefix. Expected format: {prefix}-{env}-app-wsky (e.g., plateng-prod-app-wsky)',
          );
        }
        onChange(''); // Clear the output when prefix can't be extracted
      } else {
        // Field is empty - that's OK since it's optional
        setValidationError('');
        onChange('');
      }
    },
    [onChange],
  );

  // Clear field when it should no longer be visible
  // This prevents stale projectPrefix values from persisting in form data
  useEffect(() => {
    if (!shouldBeVisible && formData) {
      setProjectId('');
      setValidationError('');
      // Cast to unknown first since Backstage's onChange type doesn't permit undefined,
      // but passing undefined is the correct way to clear the field value
      onChange(undefined as unknown as string);
    }
  }, [shouldBeVisible, formData, onChange]);

  // Only render when templateSource is 'standard' AND standardTemplate is 'terraform-infrastructure'
  if (!shouldBeVisible) {
    return null;
  }

  const placeholder =
    uiSchema?.['ui:placeholder'] || 'e.g., plateng-prod-app-wsky';
  const description = uiSchema?.['ui:description'] || schema.description;

  return (
    <FormControl
      margin="normal"
      required={required}
      error={(rawErrors && rawErrors.length > 0) || !!validationError}
      fullWidth
    >
      <TextField
        id={idSchema?.$id}
        label={schema.title || 'GCP Project ID'}
        value={projectId}
        onChange={handleChange}
        placeholder={placeholder}
        required={required}
        error={(rawErrors && rawErrors.length > 0) || !!validationError}
        helperText={
          !validationError && !rawErrors?.length ? description : undefined
        }
        fullWidth
        inputProps={{
          maxLength: 63, // GCP project ID max length
        }}
      />

      {/* Show extracted prefix preview */}
      {extractedPrefix && (
        <Box className={classes.prefixPreview}>
          <Typography className={classes.prefixLabel}>
            Extracted prefix:
          </Typography>
          <Typography className={classes.prefixValue}>
            {extractedPrefix}
          </Typography>
        </Box>
      )}

      {/* Validation error message */}
      {validationError && (
        <FormHelperText error style={{ fontSize: '0.9rem', fontWeight: 500 }}>
          {validationError}
        </FormHelperText>
      )}

      {rawErrors && rawErrors.length > 0 && (
        <FormHelperText error style={{ fontSize: '0.9rem', fontWeight: 500 }}>
          {rawErrors.join(', ')}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export const GcpProjectIdFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'GcpProjectIdField',
    component: GcpProjectIdField,
  }),
);
